import React, { Component } from 'react';
import moment from 'moment';
import API from '../../utils/API';
import SaveBar from './components/SaveBar';

const DEFAULT = {
	datesFlexible: false,
	flexibilityNotes: '',
	dailyGrid: []
};

class RoomBlocks extends Component {
	constructor(props) {
		super(props);
		this.state = {
			data: { ...DEFAULT, ...(props.request?.roomBlock || {}) },
			editing: false,
			saving: false
		};
	}

	update = (field, value) => {
		this.setState((s) => ({ data: { ...s.data, [field]: value }, editing: true }));
	};

	addDay = () => {
		this.setState((s) => {
			const grid = [...(s.data.dailyGrid || [])];
			let nextDate = null;
			if (grid.length > 0 && grid[grid.length - 1].date) {
				nextDate = moment(grid[grid.length - 1].date).add(1, 'days').format('YYYY-MM-DD');
			} else if (this.props.request?.eventDetails?.arrivalDate) {
				nextDate = moment(this.props.request.eventDetails.arrivalDate).format('YYYY-MM-DD');
			}
			grid.push({ date: nextDate, king: 0, double: 0, suite: 0, staff: 0, total: 0, notes: '' });
			return { data: { ...s.data, dailyGrid: grid }, editing: true };
		});
	};

	duplicateDay = (i) => {
		this.setState((s) => {
			const grid = [...(s.data.dailyGrid || [])];
			grid.splice(i + 1, 0, { ...grid[i] });
			return { data: { ...s.data, dailyGrid: grid }, editing: true };
		});
	};

	removeDay = (i) => {
		this.setState((s) => {
			const grid = [...(s.data.dailyGrid || [])];
			grid.splice(i, 1);
			return { data: { ...s.data, dailyGrid: grid }, editing: true };
		});
	};

	updateDay = (i, field, value) => {
		this.setState((s) => {
			const grid = [...(s.data.dailyGrid || [])];
			const parsedValue = field !== 'date' && field !== 'notes' ? parseInt(value, 10) || 0 : value;
			grid[i] = { ...grid[i], [field]: parsedValue };
			if (['king', 'double', 'suite', 'staff'].includes(field)) {
				const r = grid[i];
				r.total = (parseInt(r.king, 10) || 0) + (parseInt(r.double, 10) || 0) + (parseInt(r.suite, 10) || 0) + (parseInt(r.staff, 10) || 0);
			}
			return { data: { ...s.data, dailyGrid: grid }, editing: true };
		});
	};

	save = async () => {
		this.setState({ saving: true });
		try {
			await API().patch(
				`Organizations/${this.props.orgId}/proposal-requests/${this.props.requestId}`,
				{ roomBlock: this.state.data }
			);
			this.setState({ editing: false, saving: false });
			this.props.refreshSectionCounts && this.props.refreshSectionCounts();
		} catch (e) {
			console.error('Error saving room blocks:', e);
			this.setState({ saving: false });
		}
	};

	cancel = () => {
		this.setState({
			data: { ...DEFAULT, ...(this.props.request?.roomBlock || {}) },
			editing: false
		});
	};

	render() {
		const { data, editing, saving } = this.state;
		const { request } = this.props;
		const grid = data.dailyGrid || [];

		const peakRow = grid.length ? grid.reduce((best, d) => (d.total || 0) > (best.total || 0) ? d : best, grid[0]) : null;

		return (
			<>
				{/* ── Date Flexibility ─────────────────────────── */}
				<h2 className="pr-subsection-title" style={{ marginTop: 0 }}>Date Flexibility</h2>

				<div className="pr-field-group">
					<button
						type="button"
						className={`pr-toggle ${data.datesFlexible ? 'on' : ''}`}
						onClick={() => this.update('datesFlexible', !data.datesFlexible)}
					>
						<span className="pr-toggle-track" />
						<span>Event dates are flexible</span>
					</button>
				</div>

				{data.datesFlexible && (
					<div className="pr-field-group">
						<label className="pr-field-label">Flexibility Notes</label>
						<textarea
							className="pr-textarea"
							rows={3}
							value={data.flexibilityNotes || ''}
							onChange={(e) => this.update('flexibilityNotes', e.target.value)}
							placeholder="+/- a week, weekdays preferred, etc."
						/>
					</div>
				)}

				{/* ── Room Block Table ──────────────────────────── */}
				<div className="pr-subsection-head">
					<h2 className="pr-subsection-title">Room Block</h2>
					{grid.length > 0 && (
						<span className="pr-save-bar-meta">
							{grid.length} night{grid.length !== 1 ? 's' : ''} · peak {peakRow?.total || 0} rooms
						</span>
					)}
				</div>

				{grid.length === 0 ? (
					<div className="pr-section-empty" style={{ marginBottom: 12 }}>
						<i className="las la-bed" />
						No nights added yet. Click "Add a night" to build the room block.
					</div>
				) : (
					<div className="pr-rb-table-wrap">
						{/* Header */}
						<div className="pr-rb-header">
							<div className="pr-rb-col pr-rb-date">Date</div>
							<div className="pr-rb-col pr-rb-num">King</div>
							<div className="pr-rb-col pr-rb-num">Double</div>
							<div className="pr-rb-col pr-rb-num">Suite</div>
							<div className="pr-rb-col pr-rb-num">Staff</div>
							<div className="pr-rb-col pr-rb-total">Total</div>
							<div className="pr-rb-col pr-rb-actions" />
						</div>

						{/* Rows */}
						{grid.map((row, i) => {
							const isPeak = peakRow && row === peakRow && row.total > 0;
							return (
								<div key={i} className="pr-rb-row">
									<div className="pr-rb-col pr-rb-date">
										<input
											className="pr-rb-date-input"
											type="date"
											value={row.date ? moment(row.date).format('YYYY-MM-DD') : ''}
											onChange={(e) => this.updateDay(i, 'date', e.target.value)}
										/>
									</div>
									{['king', 'double', 'suite', 'staff'].map((field) => (
										<div key={field} className="pr-rb-col pr-rb-num">
											<input
												className="pr-rb-num-input"
												type="number"
												min="0"
												value={row[field] || 0}
												onChange={(e) => this.updateDay(i, field, e.target.value)}
											/>
										</div>
									))}
									<div className="pr-rb-col pr-rb-total">
										<div className={`pr-rb-total-badge${isPeak ? ' highlight' : ''}`}>
											{row.total || 0}
										</div>
									</div>
									<div className="pr-rb-col pr-rb-actions">
										<button
											type="button"
											className="pr-rb-action-btn"
											onClick={() => this.duplicateDay(i)}
											title="Duplicate"
										>
											<i className="las la-copy" />
										</button>
										<button
											type="button"
											className="pr-rb-action-btn"
											onClick={() => this.removeDay(i)}
											title="Remove"
										>
											<i className="las la-times" />
										</button>
									</div>
								</div>
							);
						})}

						{/* Footer totals */}
						{(() => {
							const totKing   = grid.reduce((s, r) => s + (parseInt(r.king,   10) || 0), 0);
							const totDouble = grid.reduce((s, r) => s + (parseInt(r.double, 10) || 0), 0);
							const totSuite  = grid.reduce((s, r) => s + (parseInt(r.suite,  10) || 0), 0);
							const totStaff  = grid.reduce((s, r) => s + (parseInt(r.staff,  10) || 0), 0);
							const totAll    = totKing + totDouble + totSuite + totStaff;
							return (
								<div className="pr-rb-row" style={{ borderTop: '2px solid #e5e7eb', background: '#f9fafb', fontWeight: 600 }}>
									<div className="pr-rb-col pr-rb-date" style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>
										Totals
									</div>
									{[totKing, totDouble, totSuite, totStaff].map((val, i) => (
										<div key={i} className="pr-rb-col pr-rb-num" style={{ fontSize: 14 }}>{val}</div>
									))}
									<div className="pr-rb-col pr-rb-total">
										<div className="pr-rb-total-badge" style={{ background: '#1a1a1a', color: '#fff' }}>{totAll}</div>
									</div>
									<div className="pr-rb-col pr-rb-actions" />
								</div>
							);
						})()}
					</div>
				)}

				<button type="button" className="pr-add-btn" onClick={this.addDay}>
					<i className="las la-plus" />
					Add a night
				</button>

				<SaveBar
					editing={editing}
					saving={saving}
					onSave={this.save}
					onCancel={this.cancel}
					updatedAt={request?.updatedAt}
				/>
			</>
		);
	}
}

export default RoomBlocks;
